import { useState, useMemo, useCallback } from 'react';
import { MaterialData } from '../../../../../../types/materials';

interface GasFilters {
  status: string[];
  serialNumber: string[];
  lotNumber: string[];
  manufacturer: string[];
  batchNumber: string[];
}

interface UseGasFiltersProps {
  materials: MaterialData[];
}

export const useGasFilters = ({ materials }: UseGasFiltersProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<GasFilters>({
    status: [],
    serialNumber: [],
    lotNumber: [],
    manufacturer: [],
    batchNumber: [],
  });

  // Extract unique serial numbers from materials
  const uniqueSerialNumbers = useMemo(() => {
    const serialNumbers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.gasSerialNumber) {
        serialNumbers.add(material.details.gasSerialNumber);
      }
    });
    return Array.from(serialNumbers).sort();
  }, [materials]);

  // Extract unique lot numbers from materials
  const uniqueLotNumbers = useMemo(() => {
    const lotNumbers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.gasLotNumber) {
        lotNumbers.add(material.details.gasLotNumber);
      }
    });
    return Array.from(lotNumbers).sort();
  }, [materials]);

  // Extract unique manufacturers from materials
  const uniqueManufacturers = useMemo(() => {
    const manufacturers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.gasManufacturer) {
        manufacturers.add(material.details.gasManufacturer);
      }
    });
    return Array.from(manufacturers).sort();
  }, [materials]);

  // Extract unique batch numbers from materials
  const uniqueBatchNumbers = useMemo(() => {
    const batchNumbers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.gasBatchNumber) {
        batchNumbers.add(material.details.gasBatchNumber);
      }
    });
    return Array.from(batchNumbers).sort();
  }, [materials]);

  // Filter materials based on search query and filters
  const filteredMaterials = useMemo(() => {
    return materials.filter((material: MaterialData) => {
      // Search query filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        material.materialId?.toString().includes(searchLower) ||
        material.details?.gasSerialNumber?.toLowerCase().includes(searchLower) ||
        material.details?.gasLotNumber?.toLowerCase().includes(searchLower) ||
        material.details?.gasManufacturer?.toLowerCase().includes(searchLower) ||
        material.details?.gasBatchNumber?.toLowerCase().includes(searchLower) ||
        material.assemblyNumbers?.some((assembly: string) => assembly?.toLowerCase().includes(searchLower)) ||
        material.materialStatus?.toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus = filters.status.length === 0 || 
        filters.status.includes(material.materialStatus || '');

      // Serial number filter
      const matchesSerialNumber = filters.serialNumber.length === 0 || 
        (material.details?.gasSerialNumber && filters.serialNumber.includes(material.details.gasSerialNumber));

      // Lot number filter
      const matchesLotNumber = filters.lotNumber.length === 0 || 
        (material.details?.gasLotNumber && filters.lotNumber.includes(material.details.gasLotNumber));

      // Manufacturer filter
      const matchesManufacturer = filters.manufacturer.length === 0 || 
        (material.details?.gasManufacturer && filters.manufacturer.includes(material.details.gasManufacturer));

      // Batch number filter
      const matchesBatchNumber = filters.batchNumber.length === 0 || 
        (material.details?.gasBatchNumber && filters.batchNumber.includes(material.details.gasBatchNumber));

      return matchesSearch && matchesStatus && matchesSerialNumber && 
             matchesLotNumber && matchesManufacturer && matchesBatchNumber;
    });
  }, [materials, searchQuery, filters]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchQuery !== '' || 
           filters.status.length > 0 || 
           filters.serialNumber.length > 0 ||
           filters.lotNumber.length > 0 ||
           filters.manufacturer.length > 0 ||
           filters.batchNumber.length > 0;
  }, [searchQuery, filters]);

  // Handle filter changes
  const onFilterChange = useCallback((key: keyof GasFilters) => (value: string[]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Reset all filters
  const onResetFilters = useCallback(() => {
    setSearchQuery('');
    setFilters({
      status: [],
      serialNumber: [],
      lotNumber: [],
      manufacturer: [],
      batchNumber: [],
    });
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    uniqueSerialNumbers,
    uniqueLotNumbers,
    uniqueManufacturers,
    uniqueBatchNumbers,
    filteredMaterials,
    hasActiveFilters,
    filteredCount: filteredMaterials.length,
    onResetFilters,
  };
}; 